/**
 * Form schema for an emergency-contact row (PROD-729).
 *
 * Wire shape locked from `emergency_contact_model.dart`. The form
 * collects name + relation + phone + isProxy, plus the address block
 * (address / apartment + derived city / country / lat / lng) — F-PAT-242,
 * mirroring Flutter `create_emeregency_contact_widget.dart` (Address
 * LocationPicker + Apartment). `building` is not collected by the Flutter
 * EC widget, so it stays null on the wire.
 *
 * Required-field policy mirrors the Flutter enable-save gate:
 *   - firstName, lastName, contactNumber, relation.id are mandatory
 *   - At most one row per list can carry `isProxy=true` (enforced at
 *     the list level — see `emergency-contacts-section.tsx`)
 */

import { z } from 'zod';

export const emergencyContactRowSchema = z.object({
  /** Server id. Empty string `""` for newly-added rows. */
  emergencyContactId: z.string(),
  firstName: z.string().trim().min(1, 'First name is required'),
  middleName: z.string().nullable(),
  lastName: z.string().trim().min(1, 'Last name is required'),
  contactNumber: z.string().trim().min(1, 'Contact number is required'),
  /** Relation `{id, name}` — id mandatory, name carried for display. */
  relation: z
    .object({
      id: z.string().nullable(),
      name: z.string().nullable(),
    })
    .refine((v) => !!v.id, { message: 'Relationship is required' }),
  /** Mirrors `relation.name` — shipped as the bare `relationship`
   *  string on the wire alongside the full `relation` object. */
  relationship: z.string().nullable(),
  isProxy: z.boolean(),
  /** Address block (F-PAT-242). `address` is the picked main-text; `city`
   *  and `country` are derived from the autocomplete terms; `latitude` /
   *  `longitude` stay null (the `/api/Master/GetAddress` autocomplete
   *  doesn't return coords — same limitation as residential address). */
  address: z.string().nullable(),
  apartment: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  latitude: z.string().nullable(),
  longitude: z.string().nullable(),
  isDeleted: z.boolean(),
  /** UI-only — row is in inline-edit mode. NOT shipped. */
  isEdit: z.boolean(),
});

export type EmergencyContactRow = z.infer<typeof emergencyContactRowSchema>;

export function emptyContactRow(): EmergencyContactRow {
  return {
    emergencyContactId: '',
    firstName: '',
    middleName: null,
    lastName: '',
    contactNumber: '',
    relation: { id: null, name: null },
    relationship: null,
    isProxy: false,
    address: null,
    apartment: null,
    city: null,
    country: null,
    latitude: null,
    longitude: null,
    isDeleted: false,
    isEdit: true,
  };
}
